import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/utils/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ShieldX } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { useBranding } from "@/contexts/BrandingContext";
import { MarkAttemptDialog } from "@/components/t4r/MarkAttemptDialog";

const FILTERS = [
  { key: "awaiting", label: "Awaiting marking" },
  { key: "flagged", label: "AI-flagged" },
  { key: "breach", label: "Confirmed AI breach" },
  { key: "marked", label: "Marked" },
  { key: "all", label: "All" },
];

const StatusBadges = ({ a }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    {a.marking_status === "awaiting"
      ? <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Awaiting marking · {a.score_pct}% provisional</Badge>
      : <Badge variant="outline" className={`text-[10px] ${a.passed ? "bg-green-100 text-green-700 border-green-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{a.score_pct}% {a.passed ? "pass" : "fail"}{a.marking_status === "marked" ? " · marked" : ""}</Badge>}
    {a.ai_flags?.breach_confirmed && <Badge variant="outline" className="text-[10px] bg-rose-600 text-white border-rose-600 gap-1"><ShieldX size={10} /> AI breach</Badge>}
    {!a.ai_flags?.breach_confirmed && a.ai_flags?.any_flagged && <Badge variant="outline" className="text-[10px] bg-rose-100 text-rose-700 border-rose-200 gap-1"><AlertTriangle size={10} /> AI flagged</Badge>}
  </div>
);

export default function T4RAttemptsPage() {
  const { lms_name } = useBranding();
  useDocumentTitle("Quiz attempts", "Review and mark quiz attempts", lms_name);
  const [params, setParams] = useSearchParams();
  const filter = params.get("status") || "awaiting";
  const [attempts, setAttempts] = useState([]);
  const [marking, setMarking] = useState(null);

  const load = useCallback(() => {
    api.get(`/lms/admin/attempts?status=${filter}`).then(({ data }) => setAttempts(data.attempts || [])).catch(() => setAttempts([]));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="t4r-attempts-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight">Quiz attempts</h1>
      <div className="flex flex-wrap gap-1.5" data-testid="attempt-filters">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setParams({ status: f.key })} data-testid={`attempt-filter-${f.key}`}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${filter === f.key ? "bg-green-700 border-green-700 text-white" : "bg-white border-border text-slate-600 hover:bg-green-50"}`}>{f.label}</button>
        ))}
      </div>
      <div className="space-y-2">
        {attempts.map((a) => (
          <div key={a.id} className={`border border-border rounded-xl p-4 flex flex-wrap items-center justify-between gap-3 transition-opacity ${a.is_current ? "bg-white" : "bg-slate-50/70 opacity-70 hover:opacity-100"}`} data-testid={`attempt-${a.id}`}>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 font-medium">{a.learner_name} <span className="text-xs font-normal text-muted-foreground">({a.learner_email})</span>
                {a.is_current
                  ? <Badge className="bg-[#0b2545] text-[10px] text-white hover:bg-[#0b2545]" data-testid={`attempt-current-${a.id}`}>Current</Badge>
                  : <span className="text-[10px] font-normal italic text-slate-400" data-testid={`attempt-earlier-${a.id}`}>Earlier attempt</span>}</div>
              <p className="text-[11px] text-muted-foreground">{a.lesson_title || "Lesson quiz"} · <span className="font-medium text-slate-700" data-testid={`attempt-number-${a.id}`}>Attempt {a.attempt_no} of {a.attempt_total}</span> · {new Date(a.submitted_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })} · {new Date(a.submitted_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}{a.marked_by_name ? ` · marked by ${a.marked_by_name}` : ""}</p>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadges a={a} />
              {(a.answers || []).some((x) => x.kind === "written") && (
                <Button size="sm" variant={a.is_current ? "outline" : "ghost"} className={`h-7 text-xs ${a.is_current ? "" : "text-slate-500"}`} onClick={() => setMarking(a)} data-testid={`attempt-mark-${a.id}`}>{!a.is_current ? "View" : a.marking_status === "marked" ? "Review" : "Mark"}</Button>
              )}
            </div>
          </div>
        ))}
        {attempts.length === 0 && <p className="text-sm text-muted-foreground italic text-center py-8">No attempts here.</p>}
      </div>
      <MarkAttemptDialog attempt={marking} onClose={() => setMarking(null)} onSaved={() => { setMarking(null); load(); }} />
    </div>
  );
}
